<!--
  checus 3DS 挑战弹窗:遮罩 + 居中容器 + 内嵌第三方 ACS iframe,由 utils/checus3DSPopup.ts 挂到 body 顶层。
  只负责呈现与关闭交互;3DS 结果判定与通信在 utils/checus3DSPopup.ts。
-->
<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

defineProps<{
  /** 3DS 挑战页地址(第三方 ACS 域) */
  url: string
}>()

const emit = defineEmits<{
  /** 用户点击关闭按钮 */
  close: []
  /** iframe 元素就绪,交给外部接 IframeBridge */
  frameMounted: [el: HTMLIFrameElement]
}>()

// —— 加载态 ——

const frame = ref<HTMLIFrameElement>()
const loaded = ref(false)

// —— 生命周期:交出 iframe 引用 + 锁宿主页滚动 ——

// 弹窗可能挂在接入方的三方页面上,滚动锁定必须还原成原值而不是清空
let previousBodyOverflow = ''

onMounted(() => {
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  if (frame.value) emit('frameMounted', frame.value)
})

onBeforeUnmount(() => {
  document.body.style.overflow = previousBodyOverflow
})
</script>

<template>
  <!-- 遮罩不绑关闭:需求要求遮罩无交互,但它必须吞掉点击,不能让事件穿透到宿主页 -->
  <div :class="$style.mask">
    <div :class="$style.container">
      <div :class="$style.header">
        <button :class="$style.close" type="button" aria-label="close" @click="emit('close')">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M7 7L17 17M17 7L7 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div :class="$style.body">
        <iframe ref="frame" :class="$style.frame" :src="url" allow="payment *" @load="loaded = true"></iframe>
        <div v-if="!loaded" :class="$style.loading"><i :class="$style.spinner"></i></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.mask {
  position: fixed;
  // 用 inset 而非 100vw/100vh:vh 在 iOS Safari 按地址栏收起计算,底部会被工具栏切掉
  inset: 0;
  // 弹窗可能插入接入方的三方页面,层级要压过宿主页的一切内容
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.container {
  display: flex;
  flex-direction: column;
  // 一条规则覆盖桌面与移动端:窄屏退化成左右各 12px 边距
  width: min(500px, calc(100vw - 24px));
  height: min(648px, 85%);
  padding: 12px;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.header {
  flex: none;
  display: flex;
  justify-content: flex-end;
}

.close {
  all: unset;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #666;
  cursor: pointer;

  &:hover {
    background: #f2f3f5;
  }
}

.body {
  position: relative;
  flex: 1;
  // 不加会被 iframe 的固有高度撑破 flex 容器
  min-height: 0;
}

.frame {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
}

.loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 2px solid #e0e0e0;
  border-top-color: #999;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
